// Alias suggestions (auto-mined from the latest run) + one-click create.
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, postJson } from './client'
import { invalidate } from './nameMapping'
import type {
  AliasCandidates,
  AliasGroup,
  AliasPlan,
  AliasSuggestion,
  AliasSuggestions,
} from './types'

export function useAliasSuggestions(clientId: string) {
  return useQuery({
    queryKey: ['alias-suggestions', clientId],
    queryFn: () => api<AliasSuggestions>(`/clients/${clientId}/alias-suggestions`),
    enabled: Boolean(clientId),
  })
}

type NewAliasGroup = Pick<
  AliasSuggestion,
  'label' | 'regex_26as' | 'regex_ledger' | 'confidence'
>

export function useCreateAliasGroups(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (groups: NewAliasGroup[]) =>
      postJson<AliasGroup[]>(`/clients/${clientId}/alias-groups`, groups),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['alias-groups', clientId] })
      void qc.invalidateQueries({ queryKey: ['alias-suggestions', clientId] })
    },
  })
}

/** Decline a proposed pair — "not the same company". The record is durable
 *  (client-scoped), so the pair stops being proposed on every later read and
 *  both names fall back to their next-best pairing. */
export function useRejectPair(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: { name_26as: string; name_books: string }) =>
      postJson<{ id: number }>(`/clients/${clientId}/alias-pairs/reject`, p),
    onSuccess: () => invalidate(qc),
  })
}

// ---------------- Stage 21: building an alias against a run ----------------

export interface AliasSelection {
  names_26as: string[]
  names_books: string[]
}

/** Everything an alias can be built from in this run: the orphans on each
 *  side, and every party with what it owns and what it is worth. The party
 *  list is what lets an orphan be joined to a party that is already matched —
 *  the case aliasing exists for, and the one nothing could express before. */
export function useAliasCandidates(
  runId: string | null | undefined,
  /** rank the OTHER side against this name — the "pick left, right re-ranks"
   *  step. Omitted until something has been picked, because it scores one name
   *  against every name on the far side. */
  against?: { name: string; side: '26as' | 'books' },
) {
  const qs = new URLSearchParams()
  if (against) {
    qs.set('against', against.name)
    qs.set('side', against.side)
  }
  const suffix = qs.toString() ? `?${qs}` : ''
  return useQuery({
    queryKey: ['alias-candidates', runId, against?.name ?? '', against?.side ?? ''],
    queryFn: () => api<AliasCandidates>(`/runs/${runId}/alias-candidates${suffix}`),
    enabled: Boolean(runId),
    placeholderData: keepPreviousData,
  })
}

/** What creating the alias would cover, before creating it. The server expands
 *  the selection to whole parties, so this is also where the user finds out
 *  that picking one name pulled in three. */
export function useAliasPlan(runId: string | null | undefined, sel: AliasSelection) {
  const empty = sel.names_26as.length === 0 && sel.names_books.length === 0
  return useQuery({
    queryKey: ['alias-plan', runId, sel.names_26as, sel.names_books],
    queryFn: () => postJson<AliasPlan>(`/runs/${runId}/alias-plan`, sel),
    enabled: Boolean(runId) && !empty,
  })
}

export function useCreateAliasFromRun(runId: string, clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sel: AliasSelection & { label?: string }) =>
      postJson<{ group: AliasGroup; plan: AliasPlan }>(
        `/runs/${runId}/alias-groups`,
        sel,
      ),
    onSuccess: () => {
      // the client id is not always to hand (the party drill-through only
      // knows its run), so fall back to invalidating every alias list
      void qc.invalidateQueries({
        queryKey: Boolean(clientId) ? ['alias-groups', clientId] : ['alias-groups'],
      })
      // ONE list of what an alias moves, shared with the worklist's own
      // mutations. This hook used to refresh three alias queries and stop,
      // which left the Name mapping header cards and every category count
      // showing pre-alias figures until a reload.
      invalidate(qc)
    },
  })
}
